import { useState } from 'react';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Check, ArrowRight } from 'lucide-react';

const inputCls = 'rounded-none border-t-0 border-l-0 border-r-0 border-b border-ink/20 focus-visible:ring-0 focus-visible:border-gold px-0 text-[16px]';
const labelCls = 'uppercase tracking-luxe text-[11px] font-semibold text-ink-muted mb-2 block';

export default function CallbackModal({ open, onOpenChange }) {
  const { isAuthenticated, user } = useAuth();
  const [name, setName] = useState(isAuthenticated ? user?.name || '' : '');
  const [mobile, setMobile] = useState(isAuthenticated ? user?.phone || '' : '');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/callback-requests', { name, mobile });
      setSubmitted(true);
    } catch { /* silent fail */ }
    finally { setLoading(false); }
  };

  const handleClose = () => {
    setSubmitted(false);
    setName(isAuthenticated ? user?.name || '' : '');
    setMobile(isAuthenticated ? user?.phone || '' : '');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md rounded-none border-border bg-background p-8" data-testid="callback-modal" aria-describedby="callback-desc">
        {submitted ? (
          <div className="py-6 text-center space-y-4" data-testid="callback-success">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full border border-gold text-gold mx-auto">
              <Check className="w-5 h-5" strokeWidth={1.5} />
            </div>
            <div className="eyebrow">Received</div>
            <div className="font-display text-ink" style={{ fontSize: 28, lineHeight: 1.15 }}>
              We'll call you back soon.
            </div>
            <p id="callback-desc" className="text-ink/70" style={{ fontSize: 14, lineHeight: 1.6 }}>Our concierge team will reach out to you shortly.</p>
            <button
              onClick={handleClose}
              className="mt-6 inline-flex items-center justify-center gap-2 border border-ink text-ink hover:bg-ink hover:text-background uppercase tracking-luxe font-medium px-8 py-3 transition-all duration-300"
              style={{ fontSize: 12 }}
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <DialogHeader className="text-left">
              <div className="eyebrow mb-3">Concierge</div>
              <DialogTitle className="font-display font-light text-ink leading-none tracking-[-0.015em]" style={{ fontSize: 34 }}>
                Arrange a Callback
              </DialogTitle>
              <p id="callback-desc" className="text-ink/70 pt-2" style={{ fontSize: 14 }}>Leave your details — we'll be in touch shortly.</p>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6 pt-4" data-testid="callback-form">
              <div>
                <Label htmlFor="cb-name" className={labelCls}>Name</Label>
                <Input id="cb-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" required data-testid="callback-name-input" className={inputCls} />
              </div>
              <div>
                <Label htmlFor="cb-mobile" className={labelCls}>Mobile</Label>
                <Input id="cb-mobile" value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="+91 9876543210" required data-testid="callback-mobile-input" className={inputCls} />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="callback-submit-btn"
                className="w-full inline-flex items-center justify-center gap-3 border border-ink bg-ink text-background hover:bg-transparent hover:text-ink uppercase tracking-luxe font-medium py-4 transition-all duration-300 disabled:opacity-50"
                style={{ fontSize: 13 }}
              >
                {loading ? 'Submitting…' : (<>Request Callback <ArrowRight className="w-4 h-4" /></>)}
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
